import React, { useState } from 'react';
import { GlassWater, Info, AlertCircle, Calculator as CalcIcon, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';

const StandardDrinks = () => {
  const [volume, setVolume] = useState('');
  const [percentage, setPercentage] = useState('');
  const [standardDrinks, setStandardDrinks] = useState(null);

  const calculateStandardDrinks = () => {
    if (volume && percentage) {
      const result = (parseFloat(volume) * parseFloat(percentage) * 0.789) / 1000;
      setStandardDrinks(result.toFixed(1));
    }
  };

  const resetCalculator = () => {
    setVolume('');
    setPercentage('');
    setStandardDrinks(null);
  };

  const quickPresets = [
    { name: 'Standard Beer', volume: 330, percentage: 4 },
    { name: 'Craft Beer', volume: 330, percentage: 6 },
    { name: 'Wine Glass', volume: 150, percentage: 13 },
    { name: 'Wine Bottle', volume: 750, percentage: 13 },
    { name: 'Spirit Shot', volume: 30, percentage: 37 },
    { name: 'RTD Can', volume: 330, percentage: 5 },
  ];

  const applyPreset = (preset) => {
    setVolume(preset.volume.toString());
    setPercentage(preset.percentage.toString());
  };

  const drinkExamples = [
    { type: 'Beer (4% alcohol)', volume: '330ml bottle', image: '🍺', standard: '1.0' },
    { type: 'Wine (13% alcohol)', volume: '100ml glass', image: '🍷', standard: '1.0' },
    { type: 'Spirits (37% alcohol)', volume: '30ml shot', image: '🥃', standard: '0.9' },
    { type: 'RTD (5% alcohol)', volume: '330ml can', image: '🥤', standard: '1.3' },
    { type: 'Cider (5% alcohol)', volume: '330ml bottle', image: '🍺', standard: '1.3' },
    { type: 'Champagne (12% alcohol)', volume: '100ml glass', image: '🥂', standard: '0.9' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1707941391936-bbbfa2b15758?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-900/80 to-teal-700/80" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <GlassWater className="h-16 w-16 mx-auto mb-4" />
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Standard Drinks Calculator</h1>
          <p className="text-xl">Understanding standard drinks helps you make informed choices</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* NZ Low-Risk Drinking Guidelines Link */}
        <Card className="mb-12 bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">New Zealand Low-Risk Alcohol Drinking Guidelines</h3>
                <p className="text-gray-700">Learn about the official recommendations for safer drinking in New Zealand</p>
              </div>
              <a 
                href="https://www.alcohol.org.nz/help-advice/advice-on-alcohol/low-risk-alcohol-drinking-advice" 
                target="_blank" 
                rel="noopener noreferrer"
                className="ml-4"
              >
                <Button className="bg-teal-600 hover:bg-teal-700 text-white">
                  View Guidelines
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Calculator Section */}
        <Card className="mb-12 shadow-2xl">
          <CardHeader className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white">
            <CardTitle className="flex items-center text-2xl">
              <CalcIcon className="mr-3 h-8 w-8" />
              Calculate Your Standard Drinks
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <div className="space-y-6">
              {/* Volume Input */}
              <div>
                <Label htmlFor="volume" className="text-lg font-semibold text-gray-700 mb-2 block">
                  Volume (ml)
                </Label>
                <Input
                  id="volume"
                  type="number"
                  placeholder="e.g., 330"
                  value={volume}
                  onChange={(e) => setVolume(e.target.value)}
                  className="text-lg h-12"
                />
                <p className="text-sm text-gray-500 mt-1">Enter the volume of your drink in milliliters</p>
              </div>

              {/* Percentage Input */}
              <div>
                <Label htmlFor="percentage" className="text-lg font-semibold text-gray-700 mb-2 block">
                  Alcohol Percentage (%)
                </Label>
                <Input
                  id="percentage"
                  type="number"
                  step="0.1"
                  placeholder="e.g., 4.5"
                  value={percentage}
                  onChange={(e) => setPercentage(e.target.value)}
                  className="text-lg h-12"
                />
                <p className="text-sm text-gray-500 mt-1">Find this on the bottle or can label (e.g., 4.5% ABV)</p>
              </div>

              {/* Buttons */}
              <div className="flex gap-4">
                <Button 
                  onClick={calculateStandardDrinks} 
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white h-12 text-lg"
                  disabled={!volume || !percentage}
                >
                  Calculate
                </Button>
                <Button 
                  onClick={resetCalculator} 
                  variant="outline" 
                  className="flex-1 h-12 text-lg"
                >
                  Reset
                </Button>
              </div>

              {/* Result Display */}
              {standardDrinks !== null && (
                <div className="mt-6 p-8 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-lg border-2 border-teal-300 animate-fade-in">
                  <p className="text-center text-gray-700 text-lg mb-2">Your drink contains</p>
                  <p className="text-center text-5xl font-bold text-teal-700 mb-2">{standardDrinks}</p>
                  <p className="text-center text-gray-700 text-lg">standard drink{standardDrinks !== '1.0' ? 's' : ''}</p>
                  <Alert className="mt-4 bg-white/50">
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Your body processes approximately 1 standard drink per hour
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              {/* Quick Presets */}
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-3">Quick Presets</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {quickPresets.map((preset, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-teal-50 hover:border-teal-300 transition-all"
                      onClick={() => applyPreset(preset)}
                    >
                      <span className="font-semibold text-sm">{preset.name}</span>
                      <span className="text-xs text-gray-500">{preset.volume}ml @ {preset.percentage}%</span>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* What is a Standard Drink */}
        <Card className="mb-12 bg-gradient-to-br from-cyan-50 to-teal-50">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              <Info className="mr-3 h-8 w-8 text-cyan-600" />
              What is a Standard Drink?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-lg text-gray-700 leading-relaxed">
              A standard drink contains 10 grams of pure alcohol. This is the amount of alcohol the average person's body can process in one hour. For more information, visit <a href="https://www.alcohol.org.nz/help-advice/advice-on-alcohol/low-risk-alcohol-drinking-advice" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-800 underline">Alcohol.org.nz</a>.
            </p>
            <Alert className="bg-cyan-100 border-cyan-300">
              <AlertCircle className="h-5 w-5 text-cyan-700" />
              <AlertDescription className="text-cyan-900">
                <strong>Important:</strong> Different drinks contain different amounts of alcohol. The size of the glass or bottle matters, as does the alcohol percentage.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Standard Drink Examples */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Standard Drink Examples
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {drinkExamples.map((drink, index) => (
              <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-6 text-center">
                  <div className="text-6xl mb-4">{drink.image}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{drink.type}</h3>
                  <p className="text-gray-600 mb-3">{drink.volume}</p>
                  <div className="bg-teal-100 rounded-full px-4 py-2 inline-block">
                    <p className="text-teal-800 font-bold">{drink.standard} standard drinks</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Why It Matters */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle className="text-2xl">Why Standard Drinks Matter</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                <span className="bg-teal-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">1</span>
                Track Your Intake
              </h3>
              <p className="text-gray-700 ml-11">
                Knowing how many standard drinks you're consuming helps you stay within recommended guidelines.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                <span className="bg-teal-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">2</span>
                Pace Yourself
              </h3>
              <p className="text-gray-700 ml-11">
                Your body processes about one standard drink per hour. Understanding this helps you pace your drinking.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                <span className="bg-teal-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">3</span>
                Make Informed Choices
              </h3>
              <p className="text-gray-700 ml-11">
                When you know what you're drinking, you can make better decisions about your alcohol consumption.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Important Reminders */}
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              <AlertCircle className="mr-3 h-8 w-8 text-amber-600" />
              Important Reminders
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-gray-700 flex items-start">
              <span className="text-amber-600 mr-2 text-xl flex-shrink-0">•</span>
              <span>Drinks served at bars, restaurants, and at home are often larger than standard drink sizes</span>
            </p>
            <p className="text-gray-700 flex items-start">
              <span className="text-amber-600 mr-2 text-xl flex-shrink-0">•</span>
              <span>Wine glasses are getting bigger - a standard restaurant pour is often 150ml or more</span>
            </p>
            <p className="text-gray-700 flex items-start">
              <span className="text-amber-600 mr-2 text-xl flex-shrink-0">•</span>
              <span>Many craft beers have higher alcohol content (6-8%) than standard beer (4%)</span>
            </p>
            <p className="text-gray-700 flex items-start">
              <span className="text-amber-600 mr-2 text-xl flex-shrink-0">•</span>
              <span>Always check the alcohol percentage on the label</span>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StandardDrinks;